import { useState, useEffect, useCallback } from "react";

const shortDate = (d) =>
  new Date(d).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const normalizeStatus = (s) => {
  if (!s) return "draft";
  const lower = s.toLowerCase().replace(/[\s_-]+/g, "");
  if (lower === "inreview") return "in_review";
  return lower;
};

const isRejectedStatus = (status) => {
  if (!status) return false;
  const normalized = status.toLowerCase().trim();
  return normalized === "rejected" || normalized === "declined";
};

const normalizeAgentContract = (ac) => ({
  ...ac,
  status: normalizeStatus(ac.status),
  commissionRecords: ac.commissionRecords ?? [],
  contractAdjustments: ac.contractAdjustments ?? [],
});

const sumAmounts = (records) =>
  (records ?? []).reduce((acc, r) => acc + (r.amount ?? 0), 0);

const sumAdjustmentAmounts = (records) =>
  (records ?? []).reduce((acc, r) => acc + (r.adjustmentAmount ?? 0), 0);

const sumRecordAdjustments = (records) =>
  (records ?? []).reduce(
    (acc, r) => acc + sumAdjustmentAmounts(r.adjustments),
    0,
  );

const mapPayoutRun = (run) => {
  const agentContracts = (run.agentContracts ?? []).map(normalizeAgentContract);
  const commissionRecords = agentContracts.flatMap(
    (ac) => ac.commissionRecords,
  );
  const chargebackRecords = (run.chargebackRecords ?? []).map((c) => ({
    ...c,
    status: normalizeStatus(c.status),
  }));
  const contractAdjustments = agentContracts.flatMap(
    (ac) => ac.contractAdjustments,
  );
  const bonusRecords = (run.bonusRecords ?? []).map((b) => ({
    ...b,
    status: normalizeStatus(b.status),
  }));
  const agentAdjustments = run.agentAdjustments ?? [];

  const commissions = sumAmounts(commissionRecords);
  const bonuses = sumAmounts(bonusRecords);
  const chargebacks = sumAmounts(chargebackRecords);
  const adjustments =
    sumAdjustmentAmounts(contractAdjustments) +
    sumRecordAdjustments(bonusRecords) +
    sumRecordAdjustments(chargebackRecords) +
    sumAdjustmentAmounts(agentAdjustments);

  const net = commissions + bonuses + chargebacks + adjustments;
  const rejectedCommissions = agentContracts.reduce(
    (acc, ac) =>
      isRejectedStatus(ac.status)
        ? acc + sumAmounts(ac.commissionRecords)
        : acc,
    0,
  );
  const rejectedBonuses = bonusRecords.reduce(
    (acc, b) => (isRejectedStatus(b.status) ? acc + (b.amount ?? 0) : acc),
    0,
  );
  const rejected = rejectedCommissions + rejectedBonuses;
  const netPayable = net - rejected;
  const status = normalizeStatus(run.status);
  const lastUpdatedBy = run.lastUpdatedBy;
  const lastUpdated = run.lastUpdated;
  const isDeclined = status === "declined" || status === "rejected";
  const detail =
    lastUpdatedBy && lastUpdated
      ? `${isDeclined ? "Declined" : "Approved"} ${shortDate(lastUpdated)} by ${lastUpdatedBy}`
      : (run.notes ?? "");
  const statements =
    (run.commissionRecords?.length ?? 0) +
    (run.chargebackRecords?.length ?? 0) +
    (run.bonusRecords?.length ?? 0);

  return {
    id: run.payoutRunId,
    name: run.name,
    periodStart: run.periodStart,
    periodEnd: run.periodEnd,
    status,
    detail,
    statements,
    commissions,
    bonuses,
    chargebacks,
    adjustments,
    rejected,
    net,
    netPayable,
  };
};

export function usePayoutRuns({
  api,
  customerCode,
  query = "",
  page = 1,
  resultsPerPage = 10,
  onError,
}) {
  const [runs, setRuns] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const createPayoutRun = useCallback(
    async ({ customerCode: code, name, startDate, endDate }) => {
      const response = await api.post(
        "/api/commission/payout-runs",
        {
          customerCode: code,
          name,
          startDate,
          endDate,
        },
        { suppressGlobalError: true },
      );
      return response.data;
    },
    [api],
  );

  const fetchRuns = useCallback(
    async (code, options) => {
      if (code === undefined) {
        setRuns([]);
        setTotalRecords(0);
        setTotalPages(0);
        setHasLoaded(false);
        return;
      }
      const search = options?.query ?? query;
      const currentPage = options?.page ?? page;
      const pageSize = options?.resultsPerPage ?? resultsPerPage;

      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        if (code) params.set("customerCode", code);
        if (search) params.set("query", search);
        params.set("Options.Page", currentPage);
        params.set("Options.ResultsPerPage", pageSize);

        try {
          const response = await api.get(
            `/api/commission/payout-runs?${params.toString()}`,
            { suppressGlobalError: true },
          );
          if (response.status === 200) {
            setRuns((response.data.results ?? []).map(mapPayoutRun));
            setTotalRecords(response.data.totalRecords ?? 0);
            setTotalPages(response.data.totalPages ?? 0);
          }
        } catch (error) {
          onError?.(error);
        }
      } finally {
        setIsLoading(false);
        setHasLoaded(true);
      }
    },
    [api, onError, query, page, resultsPerPage],
  );

  useEffect(() => {
    fetchRuns(customerCode);
  }, [customerCode, query, page, resultsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    runs,
    totalRecords,
    totalPages,
    isLoading,
    hasLoaded,
    fetchRuns,
    createPayoutRun,
  };
}
